import { useEffect, useReducer, useRef } from 'react';
import type { Location } from 'history';
// react-router's own hook, deliberately not the wrapper in ./reactRouter: only `location` and
// `listen` are needed here, and the wrapper imports the feature-flags provider, which imports the
// query-string helpers, which use this hook — importing the wrapper from here would make that a
// module cycle and crash the SSR bundle at load ("Cannot access ... before initialization").
// eslint-disable-next-line no-restricted-imports
import { useHistory } from 'react-router-dom';

type LocationSelector<T> = (location: Location) => T;
type LocationSelectorIsEqual<T> = (previous: T, next: T) => boolean;

/** The fields a selector is typically written against, so call sites can narrow without redeclaring a shape. */
export type SelectableLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;

/** Reading the query string is by far the most common selection, so it lives here rather than in five call sites. */
export const selectLocationSearch = (location: Pick<Location, 'search'>) => location.search;
export const selectLocationPathname = (location: Pick<Location, 'pathname'>) => location.pathname;
export const selectLocationHash = (location: Pick<Location, 'hash'>) => location.hash;

/**
 * Derives a value from the router location and re-renders the caller only when that value changes.
 *
 * react-router v5 publishes the location through one context, so `useLocation`, `useRouteMatch` and
 * `useParams` re-render their caller on every URL change even when nothing the caller reads has
 * changed. `React.memo` cannot stop that: context bypasses it. On the guest site "every URL change"
 * means every scroll-driven `history.replace`, so a component that only wants to know "is this my
 * slug?" re-rendered — with its whole subtree — on each scroll step.
 *
 * The history object comes from react-router's own hook (a separate context whose value never
 * changes) and `history.listen` reports each location outside React. Selecting from it and forcing
 * a render only on a changed result gives the caller a precise subscription.
 *
 * Deliberately not built on `useSyncExternalStore`: the shim is present only as a transitive
 * dependency, this app is React 17 (no concurrent rendering to tear against), and several call
 * sites pass an inline selector, which the shim's `with-selector` variant re-memoises on every
 * render anyway. Revisit together with a React 18 upgrade.
 *
 * Assumes the legacy synchronous renderer: the refs below are written during render, which a
 * concurrent renderer could discard before commit.
 *
 * One edge to know about before passing a selector that closes over a fast-changing prop: the
 * listener computes its value with the closure from the last commit and caches it against the
 * location it saw. If a navigation and a change to a closed-over value land in the same React
 * batch, the render that consumes that cache uses the pre-batch closure and is one value behind
 * until the next location change. Every selector here closes over values that are fixed per route
 * (the event handle, the admin flag), so none of them can reach it today.
 */
export function useLocationSelector<T>(selector: LocationSelector<T>, isEqual: LocationSelectorIsEqual<T> = Object.is): T {
  const history = useHistory();
  const [, forceRender] = useReducer((count: number) => count + 1, 0);

  // The listener already ran the selector to decide whether a render was needed; reuse that result
  // rather than running it again here. Selectors on this hook call `matchPath` and parse query
  // strings, and the guest site fires one location change per scroll step.
  const pending = useRef<{ location: Location; value: T } | null>(null);
  const selected = pending.current && pending.current.location === history.location ? pending.current.value : selector(history.location);
  pending.current = null;

  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  useEffect(() => {
    const check = (location: Location) => {
      const next = selectorRef.current(location);
      if (!isEqualRef.current(selectedRef.current, next)) {
        pending.current = { location, value: next };
        forceRender();
      }
    };
    check(history.location);
    return history.listen(check);
  }, [history]);

  return selected;
}
